import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AreaRestrita, Me } from '@/lib/me.js';
import { podeVer } from '@/lib/me.js';

/**
 * Artigos da central de ajuda do app. Cada um diz de que área é: quem não tem
 * acesso àquela área não vê o artigo (não faz sentido explicar o que a pessoa
 * não tem). Os textos descrevem o app como ele está — ao mudar uma tela,
 * mude o artigo junto.
 */

export type Artigo = {
  slug: string;
  titulo: string;
  resumo: string;
  categoria: 'Começando' | 'Método' | 'Conhecimento' | 'Conselheiro' | 'Conta';
  /** Sem área: todo mundo vê. */
  area?: AreaRestrita;
  /** Só para quem é conselheiro (não para sócio ou responsável). */
  soConselheiro?: boolean;
  palavras: string[];
  corpo: ReactNode;
};

const L = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-accent underline">
    {children}
  </Link>
);

export const ARTIGOS: Artigo[] = [
  {
    slug: 'primeiros-passos',
    titulo: 'Primeiros passos',
    resumo: 'O que tem no app, como o menu se organiza e por que você vê só uma parte dele.',
    categoria: 'Começando',
    palavras: ['menu', 'início', 'painel', 'plano', 'não aparece'],
    corpo: (
      <>
        <p>
          A Hack Academy reúne o <strong>método 5 Forças</strong> (diagnóstico e biblioteca), as <strong>trilhas</strong> de
          conhecimento, os <strong>eventos</strong> e, para empresas no programa, o <strong>Conselheiro</strong>.
        </p>
        <h3>O menu</h3>
        <ul>
          <li><strong>Início</strong>: o resumo — diagnósticos, trilhas em andamento, próximos eventos e certificados.</li>
          <li><strong>Método</strong>: o Diagnóstico 5 Forças da sua empresa e a Biblioteca.</li>
          <li><strong>Conhecimento</strong>: Jornadas de Crescimento, Micro-Learning e Eventos.</li>
          <li><strong>Direção</strong>: o Conselheiro, se a sua empresa está no programa ou você é conselheiro.</li>
          <li><strong>Sobre mim</strong>: seu perfil, senha e esta ajuda.</li>
        </ul>
        <h3>Por que não vejo um item?</h3>
        <p>
          O menu mostra só o que o seu plano libera. Se você comprou algo e não está vendo, fale com a equipe da Hack que
          liberou o seu acesso — a liberação é feita por ela.
        </p>
      </>
    ),
  },
  {
    slug: 'diagnostico-5-forcas',
    titulo: 'Diagnóstico 5 Forças',
    resumo: 'Como responder, chamar os sócios e ler o resultado.',
    categoria: 'Método',
    palavras: ['diagnóstico', 'forças', 'perguntas', 'convidar', 'comparar', 'radar', 'resultado'],
    corpo: (
      <>
        <p>
          São <strong>50 afirmações</strong> sobre a empresa, em 5 forças (Fundação, Pessoas, Operação, Crescimento e
          Futuro) e 10 temas. Para cada uma, diga com que frequência ela é verdade: de <em>Nunca</em> (1) a{' '}
          <em>Sempre</em> (5). Responda pela empresa como ela está hoje, não como deveria estar.
        </p>
        <h3>Passo a passo</h3>
        <ol>
          <li>Em <L to="/metodo/diagnostico">Diagnóstico 5 Forças</L>, crie ou escolha a empresa e comece.</li>
          <li>As respostas são salvas a cada tema (de cinco em cinco perguntas): dá para parar e continuar depois.</li>
          <li>Convide os outros sócios pelo e-mail — cada um responde o seu, sozinho.</li>
          <li>Com dois ou mais respondendo, <strong>Comparar</strong> mostra onde vocês enxergam a empresa diferente.</li>
        </ol>
        <h3>Como ler</h3>
        <p>
          Cada força e cada tema recebe uma nota de 0 a 10. De 7 para cima é <strong>sólida</strong>, de 4 a 7 está{' '}
          <strong>em desenvolvimento</strong>, abaixo de 4 é <strong>ponto de atenção</strong>. Comece pelos pontos de
          atenção e pelas maiores divergências entre sócios.
        </p>
      </>
    ),
  },
  {
    slug: 'trilhas-e-aulas',
    titulo: 'Jornadas, Micro-Learning e aulas',
    resumo: 'Como as trilhas funcionam, quando uma aula conta como concluída e onde ficam os certificados.',
    categoria: 'Conhecimento',
    area: 'trilhas',
    palavras: ['jornada', 'micro', 'aula', 'vídeo', 'progresso', 'concluir', 'certificado'],
    corpo: (
      <>
        <p>
          <strong>Jornadas de Crescimento</strong> são trilhas mais longas, em programas e módulos.{' '}
          <strong>Micro-Learning</strong> são aulas curtas, para consumir no intervalo do dia.
        </p>
        <h3>Progresso</h3>
        <p>
          A aula em vídeo conta como <strong>concluída quando você assiste 95%</strong> dela. O app guarda de onde você
          parou: ao voltar, o vídeo retoma dali. O progresso aparece no Início, em <em>Trilhas em andamento</em>.
        </p>
        <h3>Certificados</h3>
        <p>
          Quando a equipe da Hack emite o seu certificado de conclusão, ele aparece no Início. Cada certificado tem um
          código público que qualquer pessoa pode conferir pelo link dele.
        </p>
        <h3>O vídeo não toca</h3>
        <p>
          Os vídeos são protegidos: tocam só dentro do app, com o seu login. Recarregue a página; se continuar, confira se
          a sua rede não bloqueia vídeo (redes corporativas às vezes bloqueiam).
        </p>
      </>
    ),
  },
  {
    slug: 'eventos',
    titulo: 'Eventos',
    resumo: 'A agenda de encontros e imersões e a sua participação.',
    categoria: 'Conhecimento',
    palavras: ['evento', 'imersão', 'agenda', 'participação', 'confirmar'],
    corpo: (
      <>
        <p>
          Em <L to="/eventos">Eventos</L> estão os encontros e imersões da Hack Academy. Quando você está inscrito, o evento
          aparece como confirmado também no Início.
        </p>
        <p>A inscrição em imersões pagas é feita pela equipe da Hack, a partir da sua compra.</p>
      </>
    ),
  },
  {
    slug: 'hack-growth-ia',
    titulo: 'Hack Growth IA',
    resumo: 'O assistente: o que ele responde e de onde vêm as respostas.',
    categoria: 'Conhecimento',
    area: 'ia',
    palavras: ['ia', 'assistente', 'pergunta', 'chat'],
    corpo: (
      <>
        <p>
          O assistente responde a partir do conteúdo do método e da biblioteca da Hack Academy: ele procura no material e
          mostra o que encontrou. Pergunte como você perguntaria a um consultor — "como organizar o funil de vendas?",
          "o que é a força Operação?".
        </p>
        <p>Ele não conhece os dados da sua empresa; para isso, use o Diagnóstico e o Conselheiro.</p>
      </>
    ),
  },
  {
    slug: 'conselheiro-para-socios',
    titulo: 'Conselheiro: para sócios e responsáveis',
    resumo: 'O ciclo de 12 meses, o radar, as 4 Metas e os indicadores do mês.',
    categoria: 'Conselheiro',
    area: 'conselheiro',
    palavras: ['conselheiro', 'conselho', 'ciclo', 'radar', 'metas', 'indicador', 'sócio', 'responsável', 'pauta', 'decisão', 'preenchimento', 'manhã', 'anexo', 'arquivo', 'consentimento', 'avaliação', 'concorrente', 'conflito', 'indicar', 'indicação'],
    corpo: (
      <>
        <p>
          No <L to="/direcao/conselheiro">Conselheiro</L>, a sua empresa tem um <strong>Conselheiro Principal</strong> que
          acompanha os sócios num <strong>ciclo de 12 meses</strong>. A tela mostra quem é o conselheiro (com o e-mail
          dele), em que mês do ciclo vocês estão e o plano do ciclo.
        </p>
        <h3 id="radar">O radar</h3>
        <p>
          Três vezes no ciclo — na partida, num marco e no final — o conselheiro abre o <strong>radar</strong>. Cada{' '}
          <strong>sócio</strong> responde sozinho as 50 perguntas do Diagnóstico 5 Forças; o botão <em>Responder</em>{' '}
          aparece no seu Conselheiro. O conselheiro vê onde os sócios enxergam a empresa diferente e fecha o radar com a
          nota de cada tema e a evidência que a sustenta. Depois de fechado, você vê o radar — e, ao longo do ciclo,
          partida, marco e final sobrepostos.
        </p>
        <h3 id="metas">As 4 Metas</h3>
        <p>
          Definidas na primeira manhã (S0): <strong>financeira</strong>, <strong>operacional</strong>,{' '}
          <strong>pessoas</strong> e <strong>pessoal</strong> (do sócio). Cada uma tem de onde parte, aonde quer chegar e
          o valor atual; a barra mostra quanto do caminho já foi feito.
        </p>
        <h3 id="indicadores">Indicadores do mês</h3>
        <p>
          Poucos números que mostram a saúde da empresa. Todo mês, sócio ou responsável clica em{' '}
          <em>Informar mês</em> e coloca o valor. Fora da faixa esperada, o número fica em vermelho — e o conselheiro
          leva o assunto para a pauta. Errou? Informe o mesmo mês de novo: o valor é substituído.
        </p>
        <h3 id="mes">O mês: preenchimento, pauta e decisões</h3>
        <p>
          Antes de cada manhã, a empresa faz o <strong>preenchimento</strong> — uns 15 minutos, até 5 dias antes: informa os
          indicadores do mês, dá andamento às decisões, propõe até <strong>3 temas</strong> para a pauta e conta como foi o
          mês. O conselheiro confirma a <strong>pauta</strong>, na ordem em que será discutida.
        </p>
        <p>
          Em <strong>Anexos da manhã</strong>, junte o que ajuda o conselheiro a preparar a conversa: relatório, planilha,
          apresentação (PDF, Office, imagem, texto ou áudio, até 50 MB). Os arquivos ficam guardados com acesso restrito à
          sua empresa e ao conselheiro; quem enviou pode apagar.
        </p>
        <p>
          Cada <strong>decisão</strong> tem um responsável, um prazo e um sinal de que deu certo. Quem executa clica em{' '}
          <em>Dar andamento</em> e diz como está: em dia, em risco, bloqueada ou concluída. Passou do prazo sem concluir,
          aparece como atrasada.
        </p>
        <p>
          Os <strong>lembretes</strong> chegam por e-mail e, se o seu cadastro tem telefone, também por WhatsApp: 10, 7 e 5
          dias antes da manhã, se o preenchimento ainda não foi feito; e 3 dias antes do prazo de uma decisão sua, ou
          quando ela atrasa.
        </p>
        <h3 id="manha">No dia da manhã</h3>
        <p>
          Quando o conselheiro inicia a manhã, aparece no seu Conselheiro o aviso <em>A manhã do conselho está em
          andamento</em>, com o link para acompanhar: o roteiro, o bloco em discussão com o cronômetro e as decisões que vão
          sendo registradas, e se a manhã está sendo gravada. Na abertura, cada pessoa diz se consente com a gravação para a ata (e, se quiser, com o uso
          para revisão de qualidade ou formação de conselheiros); você pode marcar o seu pelo próprio app.
        </p>
        <p>
          Se a manhã for <strong>online</strong>, ela acontece no Google Meet: o botão <em>Entrar no Meet</em> aparece na
          próxima manhã, no seu Conselheiro, assim que o conselheiro cria a sala. Se a manhã for gravada, quem grava é o
          próprio Meet, e só com o consentimento de todos os presentes.
        </p>
        <p>
          Depois da manhã, em <em>Manhãs anteriores</em>, dê uma nota de 1 a 5 para ela. É assim que a Hack acompanha a
          qualidade do conselho. Quando o conselheiro valida a ata, os sócios recebem o resumo por e-mail e a ata completa
          fica na página da manhã; responsáveis veem o resumo.
        </p>
        <h3 id="concorrentes">Concorrentes</h3>
        <p>
          No seu Conselheiro, em <em>Concorrentes</em>, os sócios listam as empresas que disputam os mesmos clientes que
          vocês (com o CNPJ, quando souber). Um conselheiro que já atende um concorrente de vocês, ou que trabalhou para um
          deles nos últimos 2 anos, não é vinculado à sua empresa sem uma liberação justificada da Hack. A lista é vista
          pelos sócios e pelo seu conselheiro; responsáveis não veem.
        </p>
        <h3 id="balanco">O balanço do ciclo</h3>
        <p>
          No fim dos 12 meses, o Conselheiro Principal publica o <strong>balanço do ciclo</strong>: o radar de partida ao
          lado do de agora, as Metas, as decisões cumpridas, os indicadores, as manhãs e o parecer dele sobre o ano. Os
          sócios recebem um e-mail e o balanço aparece no seu Conselheiro, com o botão <em>Baixar PDF</em>. Os balanços dos
          ciclos anteriores continuam ali.
        </p>
        <h3 id="indicar">Indicar uma empresa</h3>
        <p>
          Em <em>Indique uma empresa</em>, no fim da página, o sócio indica uma empresa que ganharia com um conselho: o nome,
          quem procurar (com e-mail ou telefone) e, se quiser, por quê. A indicação chega à equipe comercial da Hack, que
          entra em contato em seu nome, e você acompanha ali mesmo: recebida, em conversa, virou cliente ou não seguiu.
        </p>
        <h3>Sócio ou responsável?</h3>
        <p>
          <strong>Sócio</strong> participa do conselho, responde o radar e vê tudo da empresa. <strong>Responsável</strong>{' '}
          cuida das decisões que lhe forem atribuídas e informa indicadores; não responde o radar. O cadastro é feito pela
          equipe da Hack.
        </p>
      </>
    ),
  },
  {
    slug: 'conselheiro-para-conselheiros',
    titulo: 'Conselheiro: para conselheiros',
    resumo: 'Abrir e fechar o radar, ler as divergências, definir metas e indicadores.',
    categoria: 'Conselheiro',
    area: 'conselheiro',
    soConselheiro: true,
    palavras: ['conselheiro', 'principal', 'radar', 'divergência', 'evidência', 'metas', 'indicador', 's0', 'pauta', 'decisão', 'manhã', 'agendar', 'anexo', 'arquivo', 'conduzir', 'cronômetro', 'apresentação', 'consentimento', 'desvio', 'gravação', 'gravar', 'microfone', 'dossiê', 'formação', 'biblioteca', 'trilha', 'prova', 'certificação', 'renovação', 'conflito', 'vínculos', 'declaração', 'concorrente', 'indicar', 'indicação'],
    corpo: (
      <>
        <p>
          Em <L to="/direcao/conselheiro">Conselheiro</L> estão as empresas que você aconselha. O botão{' '}
          <strong>Radar</strong> abre a página da empresa: radares, plano do ciclo e indicadores.
        </p>
        <h3 id="papeis">Papéis</h3>
        <p>
          Só o <strong>Principal</strong> abre e fecha o radar e define metas e indicadores. <strong>Sênior</strong> e{' '}
          <strong>Convidado</strong> acompanham tudo, sem alterar.
        </p>
        <h3 id="radar">O radar</h3>
        <ol>
          <li>
            <strong>Abrir</strong>: um aberto por vez; partida e final, uma vez por ciclo; marco e final só depois da
            partida fechada.
          </li>
          <li>
            <strong>Acompanhar</strong>: você vê quem já respondeu (e quantas perguntas), o radar de cada sócio sobreposto,
            a média e os temas em que eles <strong>divergem</strong> — diferença de 2 pontos ou mais entre o maior e o
            menor. Divergência é pauta: os sócios não estão vendo a mesma empresa.
          </li>
          <li>
            <strong>Fechar</strong>: a nota de cada tema começa na média dos sócios; ajuste pelo que você viu. Registre a{' '}
            <strong>evidência</strong> — um fato ou um indicador da empresa. A nota final é sua: o autodiagnóstico é ponto de
            partida, não veredito. Fechado, o radar não muda e os sócios param de responder.
          </li>
        </ol>
        <h3 id="mes">O mês</h3>
        <ol>
          <li>
            <strong>Agende a manhã</strong> (S0, mensal, marco ou fechamento). O prazo do preenchimento da empresa é calculado:
            5 dias antes.
          </li>
          <li>
            Acompanhe o <strong>preenchimento</strong>: o relato do mês, os indicadores e o andamento das decisões. Atrasado,
            aparece em vermelho.
          </li>
          <li>
            Monte a <strong>pauta</strong>: a empresa propõe até 3 temas; você propõe os seus, marca os que entram, ordena e
            confirma. O que ficou de fora vira <em>adiado</em>.
          </li>
          <li>
            Veja os <strong>anexos da manhã</strong> que a empresa juntou, e junte os seus. Você pode apagar qualquer anexo
            da empresa que acompanha.
          </li>
          <li>
            Na manhã, <strong>registre as decisões</strong> com responsável, prazo e sinal de sucesso. Ao marcar a manhã como
            realizada, os temas da pauta viram discutidos; cancelada, os temas voltam a esperar a próxima.
          </li>
        </ol>
        <p>
          Os <strong>lembretes</strong> saem sozinhos, por e-mail e — para quem tem telefone no cadastro — por WhatsApp. A empresa recebe os do preenchimento (D-10, D-7 e D-5) e
          os das decisões (3 dias antes do prazo e quando atrasam). Você recebe um aviso quando a pauta ainda não foi
          confirmada a 2 dias da manhã e quando um indicador é informado fora da faixa esperada.
        </p>
        <h3 id="vinculos">Seus vínculos declarados</h3>
        <p>
          Em <L to="/direcao/conselheiro">Conselheiro</L>, liste as empresas em que você é, ou foi nos últimos 2 anos,
          sócio, administrador, empregado ou consultor, e confirme que a lista está completa. Antes de vincular uma empresa
          nova a você, a gestão do programa confere essa lista e as empresas que você já atende contra os concorrentes que
          os sócios informaram. Com conflito, o vínculo não acontece, a não ser que a Hack libere com uma justificativa.
          Mudou algo? Atualize e confirme de novo.
        </p>
        <h3 id="balanco">O balanço do ciclo</h3>
        <p>
          A partir do 11º mês, a página da empresa abre o <strong>balanço do ciclo</strong>: os números dos 12 meses (radar
          de partida contra o final, Metas, decisões, indicadores e manhãs), que se atualizam até a publicação. O Principal
          escreve o parecer aos sócios e a recomendação à gestão (renovar, renovar com ajustes ou encerrar, com o porquê) e
          publica: os números congelam e os sócios recebem o e-mail. A recomendação os sócios não veem; quem decide a
          renovação é a gestão do programa. Faltando 45 dias para o fim, quem não publicou recebe um lembrete. Feche o radar
          final antes, para o balanço ficar completo.
        </p>
        <h3 id="indicar">Indicar uma empresa</h3>
        <p>
          Você também pode indicar empresas, em <em>Indique uma empresa</em>, no fim da página do Conselheiro. A indicação
          chega à equipe comercial da Hack e você acompanha a situação ali.
        </p>
        <h3 id="formacao">A formação e a Biblioteca do método</h3>
        <p>
          A trilha de <strong>formação do conselheiro</strong> é liberada pelo seu perfil (em formação ou certificado),
          sem depender de plano: ela aparece em <em>Jornadas de Crescimento</em> e no topo da{' '}
          <strong>Biblioteca do método</strong>, no menu Direção. Na Biblioteca, as aulas ficam organizadas pelas 5 Forças
          e pelos 10 temas, com busca e a marca do que você já viu — para consultar ao preparar uma manhã. O dossiê leva
          direto ao tema de cada assunto da pauta.
        </p>
        <h3 id="certificacao">A prova de certificação</h3>
        <p>
          Em <em>Certificação</em>, no menu Direção, você faz a prova online: 20 questões objetivas sobre o método e 2
          cases — uma situação de empresa em que você enquadra o problema no tema do método e escreve a decisão que levaria
          ao conselho. São 120 minutos do início à entrega; as respostas ficam salvas enquanto você responde, e se o tempo
          acabar a prova é entregue com o que estiver respondido. A objetiva precisa de 70% de acerto; os cases são
          avaliados pela equipe da Hack, e você recebe o resultado por e-mail, com o parecer de cada case no app. Não
          aprovou: nova tentativa em 7 dias, até 3 por nível em 12 meses. A renovação anual é a prova inteira de novo, num
          nível mais profundo, e abre 90 dias antes do vencimento.
        </p>
        <p>
          Aprovado na prova, falta a <strong>sessão supervisionada</strong>, que a equipe da Hack combina com você. Quando
          ela é registrada, o certificado sai na hora, válido por 1 ano, com uma página pública de validação. Você recebe
          avisos 60, 30 e 7 dias antes de vencer; renovar antes não encurta o ano. Vencida, você continua com as empresas que
          já atende, mas não pode ser Principal de uma empresa nova até renovar.
        </p>
        <h3 id="preparar">Preparar a manhã: o dossiê</h3>
        <p>
          Quando a pauta é confirmada, o app prepara o <strong>dossiê de preparação</strong>: para cada tema, o
          enquadramento no método, hipóteses de causa e perguntas para fazer aos sócios, e o que pedir ou olhar antes;
          da empresa, um panorama, os alertas (indicadores fora da faixa, decisões atrasadas, metas longe do alvo) e
          onde os sócios enxergam diferente. Ele fica na página da manhã, pelo link <em>Dossiê de preparação</em> da
          próxima manhã, e pode ser impresso. O Principal gera de novo quando quiser; se a pauta mudar depois, a tela
          avisa. Na condução, as perguntas de cada tema aparecem no tema em discussão. O dossiê é só dos conselheiros:
          os sócios não veem, e as hipóteses são para testar na conversa, não conclusões.
        </p>
        <h3 id="conduzir">Conduzir a manhã</h3>
        <ol>
          <li>
            Na próxima manhã, clique em <strong>Iniciar a manhã</strong>. A pauta confirmada vira o roteiro em blocos —
            abertura, números do mês, andamento das decisões, um bloco por tema e fechamento — e o cronômetro começa.
          </li>
          <li>
            Na <strong>abertura</strong>, marque quem está presente e o consentimento de cada um. Só dá para gravar com o
            consentimento de todos os presentes para a ata.
          </li>
          <li>
            Avance com <strong>Próximo bloco</strong> ou clique em qualquer bloco do roteiro. O tempo de cada bloco fica
            vermelho quando passa do previsto.
          </li>
          <li>
            O que surgir fora da pauta, registre como <strong>desvio</strong>, já com o tema do método. As decisões são
            capturadas no tema em discussão, com responsável, prazo e sinal de sucesso.
          </li>
          <li>
            Para <strong>gravar</strong>, use o painel Gravação, no alto da condução: com o consentimento de todos os
            presentes, clique em <em>Gravar</em> e autorize o microfone. O áudio sobe em fatias de 5 minutos enquanto a
            manhã segue — deixe o aparelho perto da mesa, ligado na tomada; a tela não apaga. Para um assunto que não deve
            ser gravado, use <em>Fora da gravação</em> e depois <em>Voltar a gravar</em>: o app guarda só que houve o
            trecho, e quando. Se alguém retirar o consentimento, a gravação para sozinha. Depois da manhã, só você ouve a
            gravação, para conferir a ata.
          </li>
          <li>
            Manhã <strong>online</strong>: antes, conecte o Google no HackOS (<em>Configurações › Conexões</em>, com a
            sua conta da Hack) e clique em <em>Criar a sala do Meet</em> na próxima manhã; os sócios entram pelo mesmo
            link. Na condução, quem grava é o Meet: com o consentimento de todos os presentes (a tela avisa), ligue
            em <em>Atividades › Gravação</em>, marcando <em>Também transcrever</em>. Para um assunto fora da gravação,
            pare a gravação no Meet. Depois de encerrar, a gravação e a transcrição, com quem falou, vêm para o app
            sozinhas, em minutos ou poucas horas, e os originais vão para a lixeira do seu Drive. Sem o consentimento
            de todos, nada do Meet vem para o app.
          </li>
          <li>
            O <strong>modo apresentação</strong> põe a tela em tela cheia para a TV da sala: o bloco atual, o tempo e o que
            já foi decidido, sem os controles.
          </li>
          <li>
            No fechamento, <strong>encerre a manhã</strong>. Os sócios dão a nota dela, e você vê a média.
          </li>
          <li>
            Alguns minutos depois, a <strong>ata</strong> aparece na página da manhã como rascunho: a IA transcreve a
            gravação e escreve a ata, um resumo curto e as decisões que ficaram de fora da captura. Revise e edite o texto,
            registre ou descarte cada decisão sugerida e clique em <em>Validar a ata</em>. Só então os sócios veem a ata e
            recebem o resumo por e-mail. Sem gravação, escreva a ata você mesmo ou peça o rascunho só com a pauta e as
            decisões. O áudio é apagado 90 dias depois da validação; a ata fica.
          </li>
        </ol>
        <h3 id="metas">As 4 Metas e os indicadores</h3>
        <p>
          Na S0, defina com os sócios uma meta de cada tipo (base, alvo e unidade; o alvo pode ser menor que a base, como
          num prazo) e de três a cinco <strong>indicadores mensais</strong>, cada um ligado a um tema do radar e com a
          faixa esperada. A empresa informa o valor todo mês; fora da faixa, você vê em vermelho. Indicador que deixou de
          fazer sentido pode ser desativado — o histórico fica.
        </p>
      </>
    ),
  },
  {
    slug: 'conta-e-acesso',
    titulo: 'Conta, senha e acesso',
    resumo: 'Entrar, trocar a senha, esqueci a senha e os acessos de suporte à sua conta.',
    categoria: 'Conta',
    palavras: ['senha', 'login', 'entrar', 'esqueci', 'perfil', 'suporte', 'sessão'],
    corpo: (
      <>
        <h3>Esqueci a senha</h3>
        <p>
          Na tela de entrada, use <L to="/esqueci-senha">Esqueci a senha</L>: chega um link por e-mail para criar uma nova.
        </p>
        <h3>Perfil e senha</h3>
        <p>
          Em <L to="/conta">Perfil</L> você muda nome, telefone, foto e senha.
        </p>
        <h3>Acessos de suporte</h3>
        <p>
          Para te ajudar, a equipe da Hack pode entrar na sua conta em <strong>modo suporte</strong>: só para ver, sem
          alterar nada, por no máximo 30 minutos. Todo acesso fica registrado e aparece para você no Perfil.
        </p>
      </>
    ),
  },
];

/** Os artigos que esta pessoa vê. */
export function artigosPara(me: Me | undefined): Artigo[] {
  return ARTIGOS.filter((a) => {
    if (a.area && !podeVer(me, a.area)) return false;
    if (a.soConselheiro && !(me?.conselheiro?.conselheiro || ['ADMIN', 'SUPER_ADMIN'].includes(me?.role ?? ''))) {
      return false;
    }
    return true;
  });
}

/** Busca sem acento e sem diferenciar maiúsculas, em título, resumo e palavras. */
export function buscarArtigos(lista: Artigo[], termo: string): Artigo[] {
  const norm = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  const t = norm(termo.trim());
  if (!t) return lista;
  return lista.filter((a) => norm([a.titulo, a.resumo, ...a.palavras].join(' ')).includes(t));
}
